// combatpro/monster/monsterStatBlock.js
//
// Combat PRO - Monster Studio preview derivation (Stage 59: Preview Runtime).
//
// The pure derivation layer behind the Monster Studio preview. It turns a studio
// model into the data the three preview modes need, so the on-screen preview and
// the print window share a single source of truth instead of two hand-built copies.
// It is a faithful migration of the legacy stat-block builders in battletracker.html
// (viStudioStatBlockHtml 11313, viStudioCardsBlock 11307, viStudioCombatProHtml
// 11340, and the print document in viStudioPreviewPrint 11380).
//
//   buildStatBlockSections(model) -> structured 5E data for React to render
//                                    (name, sub, ac, hp, speed, abilities, meta,
//                                    card sections, spells). Field selection, order,
//                                    labels, and fallbacks match the legacy exactly.
//   buildCombatProRows(model)     -> the Combat PRO inspector rows + note, derived
//                                    through the engine's studioToSelectable so it
//                                    shows the exact object an encounter slot loads.
//   buildPrintDocument(model)     -> the full standalone HTML document string for the
//                                    print window, reproducing the legacy inline CSS
//                                    and the printable stat-block markup verbatim.
//
// React renders the on-screen preview from the structured data (no innerHTML). The
// print path is the one place an HTML string is produced, because printing opens a
// NEW window and writes a document into it - exactly as the legacy did; it never
// touches the application DOM.
//
// This module is pure and read-only: it transforms a model and never mutates it,
// saves it, or calls a backend. It consumes the Stage 55 engine for ability
// modifiers and the selectable conversion; it recreates none of that logic. No
// React, no DOM, no localStorage, no fetch. No optional chaining. No em dash.

import {
  abilityModifier,
  studioToSelectable
} from './monsterStudioEngine';

var DASH = '\u2014';

// ---- 5E / printable structured sections ------------------------------------

export function buildStatBlockSections(model) {
  var s = model || {};
  var sp = s.speed || {};

  var speeds = [];
  if (sp.walk) { speeds.push(sp.walk + ' ft.'); }
  ['climb', 'swim', 'fly', 'burrow'].forEach(function(k) {
    if (sp[k]) { speeds.push(k + ' ' + sp[k] + ' ft.'); }
  });
  if (sp.hover) { speeds.push('hover'); }

  var ab = s.abilityScores || {};
  var abilities = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'].map(function(k) {
    return { key: k, score: (ab[k] != null ? ab[k] : 10), mod: abilityModifier(ab[k]) };
  });

  var meta = [];
  if (s.saves) { meta.push({ label: 'Saving Throws', value: s.saves }); }
  if (s.skills) { meta.push({ label: 'Skills', value: s.skills }); }
  if (s.resistances) { meta.push({ label: 'Damage Resistances', value: s.resistances }); }
  if (s.immunities) { meta.push({ label: 'Damage Immunities', value: s.immunities }); }
  if (s.vulnerabilities) { meta.push({ label: 'Damage Vulnerabilities', value: s.vulnerabilities }); }
  if (s.conditionImmunities) { meta.push({ label: 'Condition Immunities', value: s.conditionImmunities }); }
  if (s.passivePerception) { meta.push({ label: 'Senses', value: 'passive Perception ' + s.passivePerception }); }
  if (s.language) { meta.push({ label: 'Languages', value: s.language }); }
  meta.push({ label: 'Challenge', value: (s.cr || DASH) + (s.xp ? (' (' + s.xp + ' XP)') : '') });

  var cardDefs = [
    ['Traits', s.traits],
    ['Actions', s.actions],
    ['Bonus Actions', s.bonusActions],
    ['Reactions', s.reactions],
    ['Legendary Actions', s.legendaryActions],
    ['Lair Actions', s.lairActions]
  ];
  var cards = [];
  cardDefs.forEach(function(def) {
    var arr = def[1];
    if (arr && arr.length) {
      cards.push({
        title: def[0],
        entries: arr.map(function(c) {
          return { name: (c && c.name) || '', desc: (c && c.desc) || '', cost: (c && c.cost) || null };
        })
      });
    }
  });

  var spellList = (s.spells && s.spells.length) ? s.spells.slice() : [];

  return {
    name: s.customName || s.name || 'Unnamed Monster',
    sub: (s.size || '') + ' ' + (s.type || '') + (s.alignment ? (', ' + s.alignment) : ''),
    ac: s.ac || DASH,
    hp: (s.hpRange || DASH) + (s.hitDice ? (' (' + s.hitDice + ')') : ''),
    speed: speeds.length ? speeds.join(', ') : DASH,
    abilities: abilities,
    meta: meta,
    cards: cards,
    spells: spellList
  };
}

// ---- Combat PRO inspector rows ---------------------------------------------

export function buildCombatProRows(model) {
  var sel = studioToSelectable(model || {});
  var rows = [
    { label: 'Name', value: sel.name },
    { label: 'Size', value: sel.size || DASH },
    { label: 'Race / Type', value: sel.race || DASH },
    { label: 'CR', value: sel.cr || DASH },
    { label: 'AC', value: sel.ac || DASH },
    { label: 'HP', value: sel.hpRange || DASH },
    { label: 'Passive Perception', value: sel.passivePerception || DASH },
    { label: 'Resistances', value: sel.resistances || DASH },
    { label: 'Immunities', value: sel.immunities || DASH },
    { label: 'Abilities mapped', value: sel.abilities.length + ' (traits + actions + bonus + reactions)' },
    { label: 'Legendary actions', value: String(sel.legendaryActions.length) },
    { label: 'Spells', value: sel.spells.length ? sel.spells.join(', ') : DASH },
    { label: 'Custom flag', value: String(sel.__custom) },
    { label: 'Studio ID', value: sel.__studioId || DASH }
  ];
  return {
    rows: rows,
    note: 'This is exactly the object Combat PRO loads into an encounter slot. Saving throws are passed as a safe empty set; traits, actions, bonus actions and reactions all flow into the Abilities list.'
  };
}

// ---- Print document (new-window HTML string) -------------------------------

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cardsBlockHtml(title, entries) {
  if (!entries || !entries.length) { return ''; }
  return '<div class="vi-sb-sec"><div class="vi-sb-sec-title">' + escapeHtml(title) + '</div>' +
    entries.map(function(c) {
      return '<p><b>' + escapeHtml(c.name) + '.</b> ' + escapeHtml(c.desc) + (c.cost ? (' <i>(Costs ' + escapeHtml(c.cost) + ')</i>') : '') + '</p>';
    }).join('') + '</div>';
}

// Reproduces viStudioStatBlockHtml(s, true) as a string from the structured data.
function statBlockHtmlString(sections, printable) {
  var abilRow = sections.abilities.map(function(a) {
    return '<div><div class="vi-sb-ab-k">' + a.key + '</div><div class="vi-sb-ab-v">' + escapeHtml(a.score) + ' (' + a.mod + ')</div></div>';
  }).join('');
  var metaHtml = sections.meta.map(function(m) {
    return '<p><b>' + escapeHtml(m.label) + '</b> ' + escapeHtml(m.value) + '</p>';
  }).join('');
  var spellsBlock = sections.spells.length
    ? '<div class="vi-sb-sec"><div class="vi-sb-sec-title">Spellcasting</div><p>' + sections.spells.map(escapeHtml).join(', ') + '</p></div>'
    : '';
  return '<div class="vi-sb' + (printable ? ' vi-sb-print' : '') + '">' +
    '<div class="vi-sb-name">' + escapeHtml(sections.name) + '</div>' +
    '<div class="vi-sb-sub">' + escapeHtml(sections.sub) + '</div>' +
    '<hr><p><b>Armour Class</b> ' + escapeHtml(sections.ac) + '</p>' +
    '<p><b>Hit Points</b> ' + escapeHtml(sections.hp) + '</p>' +
    '<p><b>Speed</b> ' + escapeHtml(sections.speed) + '</p><hr>' +
    '<div class="vi-sb-abil">' + abilRow + '</div><hr>' +
    metaHtml + '<hr>' +
    sections.cards.map(function(sec) { return cardsBlockHtml(sec.title, sec.entries); }).join('') +
    spellsBlock +
    '</div>';
}

var PRINT_CSS = 'body{font-family:Georgia,serif;color:#111;max-width:680px;margin:24px auto;padding:0 16px;} .vi-sb-name{font-size:26px;font-weight:700;color:#7a200d;} .vi-sb-sub{font-style:italic;color:#555;margin-bottom:6px;} hr{border:none;border-top:2px solid #7a200d;margin:8px 0;} .vi-sb b{color:#7a200d;} .vi-sb p{margin:3px 0;line-height:1.4;} .vi-sb-abil{display:flex;gap:10px;text-align:center;margin:6px 0;} .vi-sb-abil>div{flex:1;} .vi-sb-ab-k{font-weight:700;color:#7a200d;} .vi-sb-sec-title{font-variant:small-caps;font-weight:700;color:#7a200d;border-bottom:1px solid #7a200d;margin:10px 0 4px;}';

// Full standalone document for the print window (viStudioPreviewPrint).
export function buildPrintDocument(model) {
  var s = model || {};
  var sections = buildStatBlockSections(s);
  var title = s.customName || s.name || 'Monster';
  return '<html><head><title>' + escapeHtml(title) + '</title><style>' + PRINT_CSS + '</style></head><body>' +
    statBlockHtmlString(sections, true) + '</body></html>';
}
